import cloneDeep from 'lodash/cloneDeep';
import escape from 'lodash/escape';

import {EMAIL_MESSAGE_FIELDS, MESSAGE_MODALITIES} from '@constants/communicator';
import {
  DATA_CUSTOM_TYPE_REGEX,
  DATA_KEY_REGEX,
  DATA_TYPE_REGEX,
  STANDARD_VARIABLE_MAPPING,
  VARIABLE_CLASS_NAMES,
  VARIABLE_PILL_REGEX,
  VARIABLE_PLACEHOLDER_REGEX,
  VARIABLE_TYPE_CUSTOM,
  VARIABLE_TYPE_STANDARD,
} from '@constants/message-variables';

import horizonJSON from '@utils/json';

/**
 * Helper to extract data-key from a regex-matched span element
 * @param {Array} match - Regex match array from VARIABLE_PILL_REGEX
 * @returns {object} The extracted variable information
 */
function extractDataKey(match) {
  const spanElement = match[0];
  const keyMatch = spanElement.match(DATA_KEY_REGEX);
  const typeMatch = spanElement.match(DATA_TYPE_REGEX);
  const customTypeMatch = spanElement.match(DATA_CUSTOM_TYPE_REGEX);

  return {
    key: keyMatch ? keyMatch[1] : null,
    type: typeMatch ? typeMatch[1] : VARIABLE_TYPE_STANDARD,
    customType: customTypeMatch ? customTypeMatch[1] : null,
  };
}

/**
 * Helper to find the matching closing span tag by counting nested spans
 * @param {string} htmlString - The HTML string
 * @param {number} startIndex - Starting index after opening tag
 * @returns {number} Index of the closing tag end, or -1 if not found
 */
function findMatchingClosingSpan(htmlString, startIndex) {
  let spanDepth = 1;
  let currentIndex = startIndex;

  while (spanDepth > 0 && currentIndex < htmlString.length) {
    const nextOpenSpan = htmlString.indexOf('<span', currentIndex);
    const nextCloseSpan = htmlString.indexOf('</span>', currentIndex);

    if (nextCloseSpan === -1) return -1;

    if (nextOpenSpan !== -1 && nextOpenSpan < nextCloseSpan) {
      spanDepth++;
      currentIndex = nextOpenSpan + 5;
    } else {
      spanDepth--;
      currentIndex = nextCloseSpan + 7;
    }
  }
  return spanDepth === 0 ? currentIndex : -1;
}

/**
 * Helper to process a single variable span match and replaces it
 * @param {string} htmlString - The HTML string
 * @param {Array} match - The regex match
 * @returns {object} Object with updated string and success flag
 */
function processVariableSpan(htmlString, match) {
  const {key, type, customType} = extractDataKey(match);
  if (!key) {
    return {result: htmlString, success: false};
  }
  const startIndex = htmlString.indexOf(match[0]);
  const openTagEnd = startIndex + match[0].length;
  const closingIndex = findMatchingClosingSpan(htmlString, openTagEnd);

  if (closingIndex === -1) {
    return {result: htmlString, success: false};
  }
  const completeSpan = htmlString.substring(startIndex, closingIndex);
  const isCustom = type === VARIABLE_TYPE_CUSTOM;

  // For custom variables, include custom type if available
  const curlyFormat = isCustom ? `{{c:${key}${customType ? `:${customType}` : ''}}}` : `{{${key}}}`;

  const result = htmlString.replace(completeSpan, curlyFormat);
  return {result, success: true};
}

/**
 * Helper to add DIV tags around content if they're not already present
 * This is needed because Quill automatically adds <div></div> tags around content,
 * Needed when loading saved template from backend
 * @param {string} content - The content to wrap with div tags
 * @returns {string} The content wrapped with div tags
 */
function addDivTagsAroundContent(content) {
  const divContent = !content || typeof content !== 'string' ? '' : content.trim();
  return divContent.startsWith('<div>') && divContent.endsWith('</div>') ? divContent : `<div>${divContent}</div>`;
}

/**
 * Helper which converts variable pills to curly brace format for use in api
 * Example: <span data-key="FIRST_NAME" translate="no" contenteditable="false" class="variableEmbedPill variableEmbedPill--standard"><span contenteditable="false">First Name</span></span>
 * Becomes: {{FIRST_NAME}}
 *
 * @param {string} htmlString - The HTML string containing variable placeholders
 * @returns {string} The converted string with curly brace variables
 */
export function convertPillsToVariablePlaceholders(htmlString) {
  if (!htmlString || typeof htmlString !== 'string') {
    return htmlString;
  }
  let result = htmlString;
  let match;

  while ((match = VARIABLE_PILL_REGEX.exec(result))) {
    const processResult = processVariableSpan(result, match);
    if (!processResult.success) break;
    result = processResult.result;
  }
  return result;
}

/**
 * Converts variable pills in all relevant form fields to variable placeholder format for template saving
 * This function is applied during template save to convert Quill editor blots
 * Converts span-based pills to the {{VARIABLE_NAME}} format for storage
 *
 * @param {object} formFields - The form fields object
 * @returns {object} The form fields with converted variable placeholders
 */
export function convertPillsToVariablePlaceholdersInFormFields(formFields) {
  if (!formFields) return formFields;
  const clonedFields = cloneDeep(formFields);

  // Convert variables in the main message body (used in combined mode)
  if (clonedFields[EMAIL_MESSAGE_FIELDS.messageBody]) {
    clonedFields[EMAIL_MESSAGE_FIELDS.messageBody] = convertPillsToVariablePlaceholders(
      clonedFields[EMAIL_MESSAGE_FIELDS.messageBody],
    );
  }

  // Convert variables in the subject
  if (clonedFields[EMAIL_MESSAGE_FIELDS.subject]) {
    clonedFields[EMAIL_MESSAGE_FIELDS.subject] = convertPillsToVariablePlaceholders(
      clonedFields[EMAIL_MESSAGE_FIELDS.subject],
    );
  }

  // Convert variables in each modality format (used in separated mode)
  if (clonedFields[EMAIL_MESSAGE_FIELDS.formats]) {
    Object.values(MESSAGE_MODALITIES).forEach((modality) => {
      const modalityFormat = clonedFields[EMAIL_MESSAGE_FIELDS.formats][modality];
      if (modalityFormat) {
        // Convert message body for this modality
        if (modalityFormat[EMAIL_MESSAGE_FIELDS.messageBody]) {
          modalityFormat[EMAIL_MESSAGE_FIELDS.messageBody] = convertPillsToVariablePlaceholders(
            modalityFormat[EMAIL_MESSAGE_FIELDS.messageBody],
          );
        }
        // Convert subject for this modality (for email and push)
        if (modalityFormat[EMAIL_MESSAGE_FIELDS.subject]) {
          modalityFormat[EMAIL_MESSAGE_FIELDS.subject] = convertPillsToVariablePlaceholders(
            modalityFormat[EMAIL_MESSAGE_FIELDS.subject],
          );
        }
      }
    });
  }
  return clonedFields;
}

/**
 * Converts curly brace variables back to span-based "pills" for display in UI - used for displaying the variables in forms
 * Example: {{FIRST_NAME}} becomes:
 * <span data-key="FIRST_NAME" translate="no" contenteditable="false" class="variableEmbedPill variableEmbedPill--standard">&#xFEFF;<span contenteditable="false">First Name</span>&#xFEFF;</span>
 *
 * @param {string} text - The text containing {{VARIABLE}} placeholders
 * @returns {string} The text with variables converted to span tags
 */
export function convertCurlyBraceVariablesToPills(text) {
  return !text || typeof text !== 'string'
    ? text
    : text.replace(VARIABLE_PLACEHOLDER_REGEX, (match, customFlag, customKey, customType, standardKey) => {
        const variableInfo = STANDARD_VARIABLE_MAPPING[standardKey];
        let spanProps = {
          translate: 'no',
          contenteditable: 'false',
        };

        if (customFlag && customKey) {
          const safeKey = escape(customKey);
          spanProps = {
            ...spanProps,
            'data-key': safeKey,
            'data-type': VARIABLE_TYPE_CUSTOM,
            class: `${VARIABLE_CLASS_NAMES.PILL} ${VARIABLE_CLASS_NAMES.PILL_CUSTOM}`,
          };

          if (customType) {
            spanProps['data-custom-type'] = customType;
          }

          return `<span ${Object.entries(spanProps)
            .map(([key, value]) => `${key}="${value}"`)
            .join(' ')}>&#xFEFF;<span contenteditable="false">${safeKey}</span>&#xFEFF;</span>`;
        }

        if (variableInfo) {
          spanProps = {
            ...spanProps,
            'data-key': standardKey,
            'data-type': variableInfo.type,
            class: `${VARIABLE_CLASS_NAMES.PILL} ${variableInfo.type === VARIABLE_TYPE_STANDARD ? VARIABLE_CLASS_NAMES.PILL_STANDARD : VARIABLE_CLASS_NAMES.PILL_CUSTOM}`,
          };

          return `<span ${Object.entries(spanProps)
            .map(([key, value]) => `${key}="${value}"`)
            .join(' ')}>&#xFEFF;<span contenteditable="false">${variableInfo.label}</span>&#xFEFF;</span>`;
        }

        return match;
      });
}

/**
 * Converts curly brace variables to pills IN ALL FORM FIELDS of compose message/template drawers for display
 * This function is applied when loading saved templates from the backend
 * Calls convertCurlyBraceVariablesToPills() on all relevant form fields (the subject + body text, for combined and separate modalities)
 *
 * @param {object} formFields - The form fields object
 * @returns {object} The form fields with converted variable placeholders
 */
export function convertCurlyBraceVariablesInFormFields(formFields) {
  if (!formFields) return formFields;

  const clonedFields = cloneDeep(formFields);

  // Convert variables in the main message body (used in combined mode)
  if (clonedFields[EMAIL_MESSAGE_FIELDS.messageBody]) {
    clonedFields[EMAIL_MESSAGE_FIELDS.messageBody] = convertCurlyBraceVariablesToPills(
      clonedFields[EMAIL_MESSAGE_FIELDS.messageBody],
    );
  }

  // Convert variables in the subject and wrap the div tags to display in Quill editor
  if (clonedFields[EMAIL_MESSAGE_FIELDS.subject]) {
    const convertedSubject = convertCurlyBraceVariablesToPills(clonedFields[EMAIL_MESSAGE_FIELDS.subject]);

    clonedFields[EMAIL_MESSAGE_FIELDS.subject] = addDivTagsAroundContent(convertedSubject);
  }

  // Convert variables in each modality format (used in separated mode)
  if (clonedFields[EMAIL_MESSAGE_FIELDS.formats]) {
    Object.values(MESSAGE_MODALITIES).forEach((modality) => {
      const modalityFormat = clonedFields[EMAIL_MESSAGE_FIELDS.formats][modality];
      if (modalityFormat) {
        // Convert message body for this modality
        if (modalityFormat[EMAIL_MESSAGE_FIELDS.messageBody]) {
          const convertedMessageBody = convertCurlyBraceVariablesToPills(
            modalityFormat[EMAIL_MESSAGE_FIELDS.messageBody],
          );

          // For non-email modalities (SMS, Push, TTS), also wrap with DIV tags since they use SimpleRichTextInput
          if (modality !== MESSAGE_MODALITIES.EMAIL_FORMAT) {
            modalityFormat[EMAIL_MESSAGE_FIELDS.messageBody] = addDivTagsAroundContent(convertedMessageBody);
          } else {
            modalityFormat[EMAIL_MESSAGE_FIELDS.messageBody] = convertedMessageBody;
          }
        }
        // Convert subject for this modality (for email and push) and wrap with div tags
        if (modalityFormat[EMAIL_MESSAGE_FIELDS.subject]) {
          const convertedSubject = convertCurlyBraceVariablesToPills(modalityFormat[EMAIL_MESSAGE_FIELDS.subject]);
          modalityFormat[EMAIL_MESSAGE_FIELDS.subject] = addDivTagsAroundContent(convertedSubject);
        }
      }
    });
  }

  return clonedFields;
}

/**
 * Converts curly brace variables back to span-based "highlights" for display in UI - used for displaying the variables in forms
 * Example: {{FIRST_NAME}} becomes:
 * <span data-key="FIRST_NAME" translate="no" contenteditable="false" class="variableEmbedHighlight variableEmbedHighlight--standard">&#xFEFF;<span contenteditable="false">&lt;First Name&gt;</span>&#xFEFF;</span>
 *
 * @param {string} text - The text containing {{VARIABLE}} placeholders
 * @returns {string} The text with variables converted to span tags
 */
export function convertCurlyBraceVariablesToHighlight(text) {
  return !text || typeof text !== 'string'
    ? text
    : text.replace(VARIABLE_PLACEHOLDER_REGEX, (match, customFlag, customKey, customType, standardKey) => {
        const variableInfo = STANDARD_VARIABLE_MAPPING[standardKey];
        let spanProps = {
          translate: 'no',
          contenteditable: 'false',
        };

        if (customFlag && customKey) {
          const safeKey = escape(customKey);
          spanProps = {
            ...spanProps,
            'data-key': safeKey,
            'data-type': VARIABLE_TYPE_CUSTOM,
            class: `${VARIABLE_CLASS_NAMES.HIGHLIGHT} ${VARIABLE_CLASS_NAMES.HIGHLIGHT_CUSTOM}`,
          };

          // Add custom type attribute if it exists
          if (customType) {
            spanProps['data-custom-type'] = customType;
          }

          const innerContent = `&lt;${safeKey}&gt;`;
          return `<span ${Object.entries(spanProps)
            .map(([key, value]) => `${key}="${value}"`)
            .join(' ')}>&#xFEFF;<span contenteditable="false">${innerContent}</span>&#xFEFF;</span>`;
        }

        if (variableInfo) {
          spanProps = {
            ...spanProps,
            'data-key': standardKey,
            'data-type': variableInfo.type,
            class: `${VARIABLE_CLASS_NAMES.HIGHLIGHT} ${variableInfo.type === VARIABLE_TYPE_STANDARD ? VARIABLE_CLASS_NAMES.HIGHLIGHT_STANDARD : VARIABLE_CLASS_NAMES.HIGHLIGHT_CUSTOM}`,
          };

          const innerContent = `&lt;${variableInfo.label}&gt;`;
          return `<span ${Object.entries(spanProps)
            .map(([key, value]) => `${key}="${value}"`)
            .join(' ')}>&#xFEFF;<span contenteditable="false">${innerContent}</span>&#xFEFF;</span>`;
        }

        return match;
      });
}

/**
 * Converts "pill" variable used in quill editor to span-based "highlights", for use in displaying in Preview Modal
 * Before: <span data-key="FIRST_NAME" translate="no" contenteditable="false" class="variableEmbedPill variableEmbedPill--standard">&#xFEFF;<span contenteditable="false">First Name</span>&#xFEFF;</span>
 *
 * After: <span data-key="${standardKey}" data-type="${variableInfo.type}" translate="no" contenteditable="false" class="${VARIABLE_CLASS_NAMES.HIGHLIGHT} ${VARIABLE_CLASS_NAMES.HIGHLIGHT_STANDARD}">&#xFEFF;<span contenteditable="false">&lt;${variableInfo.label}&gt;</span>&#xFEFF;</span>;
 * Simply converts to placeholder form, the back to span to re-use logic
 *
 * @param {string} text - The text of the quill editor, typically form a specific form field
 * @returns {string} The text with variables converted to span tags
 */
export function convertPillVariableToHighlight(text) {
  if (!text || typeof text !== 'string') {
    return text;
  }

  return convertCurlyBraceVariablesToHighlight(convertPillsToVariablePlaceholders(text));
}

/**
 * Extracts standard variables from text content in placeholder format
 * @param {string} text - The text containing {{VARIABLE}} placeholders
 * @returns {object[]} Array of standard variable objects like [{type: "FIRST_NAME", placeholder: "{{FIRST_NAME}}"}]
 */
export function extractStandardVariables(text) {
  if (!text || typeof text !== 'string') {
    return [];
  }

  const standardVariables = new Set();
  const matches = text.matchAll(VARIABLE_PLACEHOLDER_REGEX);

  for (const match of matches) {
    const [, customFlag, , , standardKey] = match;
    // Only capture standard variables (not custom ones)
    if (!customFlag && standardKey) {
      standardVariables.add(
        JSON.stringify({
          type: standardKey,
          placeholder: `{{${standardKey}}}`,
        }),
      );
    }
  }

  return Array.from(standardVariables).map((item) => horizonJSON.parse(item));
}
